import type * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import * as transformers from '@attain-apps/transformers'
import { TypeAsserter } from '@attain-apps/type-guards'
import gql from 'graphql-tag'

import * as common from '../../../../common'

const DOC = gql`
  ${common.gql.dms.data.fragments.variableValue.DOC}
  query AdminScenarios($tenantId: ID!, $zoneId: ID!, $appId: ID!) {
    tenant(id: $tenantId) {
      zone(id: $zoneId) {
        app(id: $appId) {
          scenarios {
            items {
              id
              alias
              name
              description
              createdBy
              visibility
              period {
                startDate
                endDate
              }
              variables {
                ...FragmentVariableValue
              }
            }
            nextCursor
          }
        }
      }
    }
  }
`

const TRANSFORMER = graphql.transformers.OUT.default(
  graphql.transformers.OUT.select<
    graphql.schema.AdminScenariosQuery,
    ents.core.common.Response.For.Pagination<ents.core.admin.Scenario>,
    'tenant'
  >(
    'tenant',
    graphql.transformers.OUT.select(
      'zone',
      graphql.transformers.OUT.select(
        'app',
        graphql.transformers.OUT.select(
          'scenarios',
          graphql.transformers.OUT.object({
            items: graphql.transformers.OUT.default(
              graphql.transformers.OUT.list(
                graphql.transformers.OUT.object({
                  alias: graphql.transformers.OUT.validate(
                    transformers.PASS_THROUGH,
                    TypeAsserter.assertIsNotNullish,
                    { warnOnly: true },
                  ),
                  description: transformers.PASS_THROUGH,
                  createdBy: transformers.PASS_THROUGH,
                  id: graphql.transformers.OUT.validate(
                    transformers.PASS_THROUGH,
                    TypeAsserter.assertIsNotNullish,
                    { warnOnly: true },
                  ),
                  name: graphql.transformers.OUT.validate(
                    transformers.PASS_THROUGH,
                    TypeAsserter.assertIsNotNullish,
                    { warnOnly: true },
                  ),
                  period: {
                    usingFullInput: graphql.transformers.OUT.default(
                      graphql.transformers.OUT.select(
                        'period',
                        graphql.transformers.OUT.object({
                          end: {
                            usingFullInput: graphql.transformers.OUT.select(
                              'endDate',
                              // NOTE: Appending 'T00:00:00' ensures new Date()
                              // parses as local midnight, not UTC midnight. Raw
                              // date-only strings (e.g. '2023-01-19') are parsed
                              // as UTC per spec, causing a one-day shift in
                              // western/eastern timezones.
                              graphql.transformers.OUT.custom(
                                (dateStr: string | Nullish) =>
                                  dateStr
                                    ? `${dateStr}T00:00:00`
                                    : (dateStr ?? undefined),
                              ),
                            ),
                          },
                          includesEnd: () => true,
                          includesStart: () => true,
                          start: {
                            usingFullInput: graphql.transformers.OUT.select(
                              'startDate',
                              graphql.transformers.OUT.custom(
                                (dateStr: string | Nullish) =>
                                  dateStr
                                    ? `${dateStr}T00:00:00`
                                    : (dateStr ?? undefined),
                              ),
                            ),
                          },
                        }),
                      ),
                      {},
                    ),
                  },
                  variables: graphql.transformers.OUT.default(
                    common.gql.dms.data.fragments.variableValue
                      .LOOKUP_TRANSFORMER,
                    {},
                  ),
                  visibility: graphql.transformers.OUT.validate(
                    common.gql.admin.scenario.enums.scenarioVisibility
                      .TRANSFORMER,
                    TypeAsserter.assertIsNotNullish,
                    { warnOnly: true },
                  ),
                }),
                { onNullish: 'remove' },
              ),
              [],
            ),
            nextCursor: transformers.PASS_THROUGH,
          }),
        ),
      ),
    ),
  ),
  {
    items: [],
    nextCursor: undefined,
  },
)

export const execute = graphql.createExecutor<
  graphql.schema.AdminScenariosQuery,
  ents.core.common.Response.For.Pagination<ents.core.admin.Scenario>,
  graphql.schema.AdminScenariosQueryVariables
>({
  query: DOC,
  transformer: TRANSFORMER,
})
